/**
 * Filter Templates API
 *
 * Handles CRUD operations for hexagon classification filter templates.
 * Supports three filter modes (via `filter_mode` field):
 * - 'custom': Legacy filter boxes with RGBA colors and height/count ranges
 * - 'simple_filter': 1D classification using a single property with min/max ranges
 * - 'matrix_filter': 2D classification using band property + threshold property
 *
 * Note: The API uses snake_case, frontend uses camelCase. Conversion happens
 * in createFilterTemplate/updateFilterTemplate (outbound) and parseFilterTemplate (inbound).
 */

import { api } from './index.js';

const routePath = '/filter_templates';

// TODO: Confirm with backend team the correct sentinel value for "no upper bound"
// on simple filter class max. The backend requires a numeric value (no null).
// Using 999999 as a placeholder until confirmed.
const UNBOUNDED_MAX_SENTINEL = 999999;

/**
 * Slugify a label string to create a stable class ID.
 * e.g. "Very Easy" → "very_easy", "Medium" → "medium"
 * Used because the API does not persist class IDs.
 */
const slugifyLabel = label => {
	if (!label) return 'class';
	return label
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_|_$/g, '');
};

// ---------------------------------------------------------------------------
// Outbound payload builders (frontend → API)
// ---------------------------------------------------------------------------

/**
 * Build the simple_filter payload for the API.
 * Maps frontend matrixConfig (type=simple) → API SimpleFilterSchema
 *
 * Frontend shape:
 *   { property, classes: [{ label, color, min, max, pricePerM2 }] }
 *
 * API shape:
 *   { classification_property, classes: [{ label, color, min, max, price }] }
 */
const buildSimpleFilterPayload = matrixConfig => {
	if (!matrixConfig) return null;
	return {
		classification_property: matrixConfig.property || '',
		classes: (matrixConfig.classes || []).map(cls => ({
			label: cls.label,
			color: cls.color,
			min: cls.min ?? 0,
			// TODO: Sentinel value for unbounded max — confirm with backend
			max: cls.max === null || cls.max === undefined ? UNBOUNDED_MAX_SENTINEL : cls.max,
			price: cls.pricePerM2 ?? 0,
		})),
		custom_price_fields: (matrixConfig.customPriceFields || []).map(f => ({
			label: f.label,
			price: f.price ?? 0,
		})),
	};
};

/**
 * Build the matrix_filter payload for the API.
 * Maps frontend matrixConfig (type=matrix) → API MatrixFilterSchema
 *
 * Key mapping:
 *   frontend thresholds[] (one per band) → API rows[] (one per band)
 *   frontend thresholds[i].boundaries   → API rows[i].thresholds
 *   frontend bands[i]                   → API rows[i].band_value
 *   frontend thresholds[i].max          → API rows[i].max
 *   frontend customPriceFields[]        → API custom_price_fields[]
 *
 * Frontend shape:
 *   { bandProperty, thresholdProperty, bands, classes: [{ label, color, pricePerM2 }],
 *     thresholds: [{ min, boundaries, max }], customPriceFields: [{ label, price }] }
 *
 * API shape:
 *   { band_property, threshold_property, bands, classes: [{ label, color, price }],
 *     rows: [{ band_value, min, thresholds, max }], custom_price_fields: [{ label, price }] }
 */
const buildMatrixFilterPayload = matrixConfig => {
	if (!matrixConfig) return null;
	return {
		band_property: matrixConfig.bandProperty || '',
		threshold_property: matrixConfig.thresholdProperty || '',
		bands: matrixConfig.bands || [],
		classes: (matrixConfig.classes || []).map(cls => ({
			label: cls.label,
			color: cls.color,
			price: cls.pricePerM2 ?? 0,
		})),
		rows: (matrixConfig.thresholds || []).map((t, i) => ({
			band_value: matrixConfig.bands?.[i] ?? 0,
			min: t.min ?? 0,
			thresholds: (t.boundaries || []).map(b => b ?? 0),
			max:
				t.max ??
				(t.boundaries?.length
					? t.boundaries[t.boundaries.length - 1]
					: UNBOUNDED_MAX_SENTINEL),
		})),
		custom_price_fields: (matrixConfig.customPriceFields || []).map(f => ({
			label: f.label,
			price: f.price ?? 0,
		})),
	};
};

// ---------------------------------------------------------------------------
// CRUD operations
// ---------------------------------------------------------------------------

/**
 * Fetch all filter templates for the current user.
 * @param {Object} [options={}] - Query options
 * @param {number} [options.limit=50] - Max templates to return
 * @param {string} [options.search=''] - Search term for filtering
 * @returns {Promise<{filter_templates: Array, total: number}>}
 */
export const getFilterTemplates = async ({ limit = 50, search = '' } = {}) => {
	const params = {};

	if (search) params.search = search;
	params.limit = limit;

	const { data } = await api.get(routePath, { params });
	return data;
};

/**
 * Create a new filter template.
 * @param {Object} template - Template data
 * @param {string} template.title - Template name
 * @param {number} template.hexagonSize - Hexagon size in m²
 * @param {string} [template.filterType] - 'matrix' (for matrix/simple) or omitted for custom
 * @param {Object} [template.matrixConfig] - Filter configuration (for matrix/simple filters)
 * @param {Array} [template.filterBoxes] - Legacy custom filter boxes
 * @returns {Promise<Object>} Created template
 */
export const createFilterTemplate = async template => {
	const { title, hexagonSize: hexagon_size, filterBoxes, filterType, matrixConfig } = template;

	const payload = { title, hexagon_size };

	if (filterType === 'matrix' && matrixConfig) {
		if (matrixConfig.type === 'simple') {
			payload.filter_mode = 'simple_filter';
			payload.currency = matrixConfig.currency || 'EUR';
			payload.simple_filter = buildSimpleFilterPayload(matrixConfig);
		} else {
			payload.filter_mode = 'matrix_filter';
			payload.currency = matrixConfig.currency || 'EUR';
			payload.matrix_filter = buildMatrixFilterPayload(matrixConfig);
		}
	} else {
		// Legacy custom filter boxes
		payload.filter_mode = 'custom';
		payload.currency = 'EUR';
		payload.filter_boxes = filterBoxes || [];
	}

	const res = await api.post(routePath, payload);
	return res.data;
};

/**
 * Update an existing filter template.
 * @param {Object} template - Template data with uuid
 * @returns {Promise<Object>} Updated template
 */
export const updateFilterTemplate = async template => {
	const {
		uuid,
		title,
		hexagonSize: hexagon_size,
		filterBoxes,
		filterType,
		matrixConfig,
	} = template;

	const payload = { title, hexagon_size };

	if (filterType === 'matrix' && matrixConfig) {
		if (matrixConfig.type === 'simple') {
			payload.filter_mode = 'simple_filter';
			payload.currency = matrixConfig.currency || 'EUR';
			payload.simple_filter = buildSimpleFilterPayload(matrixConfig);
		} else {
			payload.filter_mode = 'matrix_filter';
			payload.currency = matrixConfig.currency || 'EUR';
			payload.matrix_filter = buildMatrixFilterPayload(matrixConfig);
		}
	} else {
		// Legacy custom filter boxes
		payload.filter_mode = 'custom';
		payload.currency = 'EUR';
		payload.filter_boxes = filterBoxes || [];
	}

	const { data } = await api.put(`${routePath}/${uuid}/update`, payload);
	return data;
};

/**
 * Delete a filter template.
 * @param {string} uuid - Template UUID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteFilterTemplate = async uuid => {
	const { data } = await api.delete(`${routePath}/${uuid}`);
	return data;
};

// ---------------------------------------------------------------------------
// Inbound parser (API → frontend)
// ---------------------------------------------------------------------------

/**
 * Convert API response to frontend format.
 * Transforms snake_case API fields to camelCase frontend fields.
 *
 * Handles three filter modes:
 * - 'custom' → returns filterBoxes
 * - 'simple_filter' → returns matrixConfig with type='simple'
 * - 'matrix_filter' → returns matrixConfig with type='matrix'
 *
 * @param {Object} template - API template response
 * @returns {Object|null} Frontend-formatted template
 */
export const parseFilterTemplate = template => {
	if (!template) return null;

	const base = {
		uuid: template.uuid,
		title: template.title,
		hexagonSize: template.hexagon_size,
		filterMode: template.filter_mode || 'custom',
		// filterType kept for backward compatibility with components that check this field
		filterType:
			template.filter_mode === 'matrix_filter' || template.filter_mode === 'simple_filter'
				? 'matrix'
				: 'custom',
	};

	if (template.filter_mode === 'simple_filter' && template.simple_filter) {
		const sf = template.simple_filter;
		base.matrixConfig = {
			type: 'simple',
			currency: template.currency || 'EUR',
			customPriceFields: (sf.custom_price_fields || []).map(f => ({
				label: f.label,
				price: f.price ?? 0,
			})),
			property: sf.classification_property || null,
			classes: (sf.classes || []).map(cls => ({
				// TODO: Class IDs are generated from labels since the API does not persist them.
				// If users reorder classes with identical labels, IDs may collide.
				id: slugifyLabel(cls.label),
				label: cls.label,
				color: cls.color,
				opacity: 0.7,
				pricePerM2: cls.price ?? 0,
				min: cls.min ?? 0,
				// TODO: Sentinel value for unbounded max — confirm with backend
				max: cls.max >= UNBOUNDED_MAX_SENTINEL ? null : cls.max,
			})),
		};
	} else if (template.filter_mode === 'matrix_filter' && template.matrix_filter) {
		const mf = template.matrix_filter;
		base.matrixConfig = {
			type: 'matrix',
			currency: template.currency || 'EUR',
			customPriceFields: (mf.custom_price_fields || []).map(f => ({
				label: f.label,
				price: f.price ?? 0,
			})),
			bandProperty: mf.band_property || null,
			thresholdProperty: mf.threshold_property || null,
			bands: mf.bands || [],
			classes: (mf.classes || []).map(cls => ({
				// TODO: Class IDs are generated from labels since the API does not persist them.
				id: slugifyLabel(cls.label),
				label: cls.label,
				color: cls.color,
				opacity: 0.7,
				pricePerM2: cls.price ?? 0,
			})),
			thresholds: (mf.rows || []).map(row => ({
				min: row.min ?? 0,
				boundaries: row.thresholds || [],
				max: row.max ?? 0,
			})),
		};
	} else {
		// Legacy custom filter boxes
		base.filterBoxes = template.filter_boxes;
	}

	return base;
};
